import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Loader2 } from "lucide-react";

// Eagerly load critical pages
import Index from "./pages/Index";
import { ContactWidget } from "./components/ContactWidget";

// Lazy load non-critical pages
const Products = lazy(() => import("./pages/Products"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const ProductRedirect = lazy(() => import("./pages/ProductRedirect"));
const CollectionMembranes = lazy(() => import("./pages/CollectionMembranes"));
const CollectionVessels = lazy(() => import("./pages/CollectionVessels"));
const ContactUs = lazy(() => import("./pages/ContactUs"));
const About = lazy(() => import("./pages/About"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Admin = lazy(() => import("./pages/Admin"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));

// Lazy load admin protected route
const AdminProtectedRoute = lazy(() => 
  import("./components/AdminProtectedRoute").then(m => ({ default: m.AdminProtectedRoute }))
);

// Optimized QueryClient with better caching
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - data stays fresh
      gcTime: 30 * 60 * 1000, // 30 minutes - cache retention (formerly cacheTime)
      refetchOnWindowFocus: false, // Don't refetch on window focus
      retry: 1, // Only retry once on failure
    },
  },
});

// Loading fallback component
const PageLoader = () => (
  <div className="flex justify-center items-center min-h-screen">
    <Loader2 className="h-8 w-8 animate-spin text-primary" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Index />} />
            <Route path="/products" element={<Products />} />
            {/* New SEO-friendly product URLs: /:category/:brand/:sku */}
            <Route path="/ro-membranes/:brand/:handle" element={<ProductDetail />} />
            <Route path="/pressure-vessels/:brand/:handle" element={<ProductDetail />} />
            {/* 301 redirect from old /product/:handle URLs */}
            <Route path="/product/:handle" element={<ProductRedirect />} />
            {/* 301 redirect from old non-hyphenated category URLs */}
            <Route path="/romembranes/:brand/:handle" element={<ProductRedirect />} />
            <Route path="/pressurevessels/:brand/:handle" element={<ProductRedirect />} />
            {/* Collection pages with new SEO-friendly URLs */}
            <Route path="/ro-membranes" element={<CollectionMembranes />} />
            <Route path="/pressure-vessels" element={<CollectionVessels />} />
            {/* 301 redirect from old collection URLs */}
            <Route path="/collections/membranes" element={<ProductRedirect />} />
            <Route path="/collections/vessels" element={<ProductRedirect />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/blog" element={<Blog />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminProtectedRoute><Admin /></AdminProtectedRoute>} />
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
        <ContactWidget />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
